import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

import { PaginationParams } from '@app/models/pagination-params';
import { ExpandViewService } from '@app/services/expand-view.service';
import { MOBILE_WIDTH } from '@app/shared/config.constants';
import { DriverCentralDetailsModalComponent } from './driver-central-details-modal/driver-central-details-modal.component';
import { DriverService } from '@app/services/driver.service';

@Component({
  selector: 'app-section-history-driver-central',
  templateUrl: './section-history-driver-central.component.html',
  styleUrls: ['./section-history-driver-central.component.scss'],
})
export class SectionHistoryDriverCentralComponent implements OnInit {
  @Input() driverId!: string;
  @Input() driverGuid!: string;
  @Input() showSmaller!: boolean;

  trainingColumns: string[] = ['formType', 'topic', 'coach', 'creationDate'];
  trainingDataFiltered: any[] = [];
  trainingList: any[] = [];
  isLoadingTraining: boolean = false;
  showViewMoreTraining: boolean = false;
  isViewMoreTraining: boolean = true;
  pageSize!: number;
  maxDisplayedRecords: number = 5;
  defaultPageSize: number = 10;

  startPageIdx!: number;
  endPageIdx!: number;
  pageEvent!: PageEvent;
  paginationParams!: PaginationParams;
  totalRecordCount: number = 0;

  isExpandViewTriggered: boolean = false;
  subscription!: Subscription;

  mobileWidth: number = MOBILE_WIDTH;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private expandViewService: ExpandViewService,
    private dialog: MatDialog,
    private driverService: DriverService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.pageSize = this.maxDisplayedRecords;
    this.setPaginatorConfig();

    if (!this.driverId) {
      this.route.queryParams.subscribe((params) => {
        if (params['idDriver']) {
          this.driverId = params['idDriver'];
          this.loadTrainingData();
        } else {
          console.warn('DriverId and idDriver not found to load data');
        }
      });
    } else {
      this.loadTrainingData();
    }

    this.subscription = this.expandViewService.expandView$.subscribe(() => {
      this.isExpandViewTriggered = true;
      this.loadMore(true);
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  // Load data from service
  loadTrainingData(): void {
    this.isLoadingTraining = true;

    // Call service to get data
    this.driverService.getDriverCoachingHistory(this.driverId).subscribe({
      next: (response) => {
        if (response && response.length > 0 && response[0].forms) {
          const data = response[0];
          this.trainingList = data.forms.map((form: any) => ({
            formType: form.formName,
            topic: form.topic,
            coach: form.createdBy,
            creationDate: new Date(form.creationDate).toLocaleDateString('en-US'),
            // Keep original data for modal
            originalData: form,
          }));

          this.totalRecordCount = this.trainingList.length;
          this.paginationParams.total = this.totalRecordCount;

          // Initially display limited number of records
          this.trainingDataFiltered = this.trainingList.slice(0, this.pageSize);
          this.setDataFilteredAndEndPageIdx();
          this.setShowViewMoreTraining();
        }
        this.isLoadingTraining = false;

        if (this.isExpandViewTriggered) {
          this.expandViewService.incrementExpandView();
          this.isExpandViewTriggered = false;
        }
      },
      error: (error) => {
        console.error('Error loading training data:', error);
        this.isLoadingTraining = false;
      },
    });
  }

  loadMore(expandViewMore: boolean = false): void {
    if (expandViewMore) {
      this.isViewMoreTraining = false;
      this.pageSize = this.trainingList.length;
      this.trainingDataFiltered = this.trainingList;
      return;
    }

    this.isViewMoreTraining = !this.isViewMoreTraining;
    this.pageEvent.pageIndex = 0;

    if (this.isViewMoreTraining) {
      this.pageSize = this.maxDisplayedRecords;
    } else {
      this.pageSize = this.defaultPageSize;
    }

    this.setPaginatorConfig();
    this.setDataFilteredAndEndPageIdx();
  }

  setShowViewMoreTraining(): void {
    if (this.totalRecordCount > this.maxDisplayedRecords) {
      this.showViewMoreTraining = true;
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageEvent = event;
    const startIdx = event.pageIndex * event.pageSize;
    const endIdx = startIdx + event.pageSize;
    this.startPageIdx = startIdx + 1;
    this.endPageIdx = endIdx < this.totalRecordCount ? endIdx : this.totalRecordCount;
    this.setDataFilteredAndEndPageIdx();
  }

  setPaginatorConfig(): void {
    this.paginationParams = {
      limit: this.pageSize,
      offset: 0,
      total: this.totalRecordCount,
    };

    this.pageEvent = {
      length: this.paginationParams.total || 0,
      pageIndex: 0,
      pageSize: this.pageSize,
    };

    this.startPageIdx = 1;
  }

  setDataFilteredAndEndPageIdx(): void {
    if (!this.endPageIdx) {
      this.endPageIdx =
        this.defaultPageSize < this.totalRecordCount
          ? this.defaultPageSize
          : this.totalRecordCount;
    }
    const startIdx = this.pageEvent.pageIndex * this.pageEvent.pageSize;
    const endIdx = Math.min(startIdx + this.pageEvent.pageSize, this.trainingList.length);
    this.trainingDataFiltered = this.trainingList.slice(startIdx, endIdx);
  }

  announceTrainingSortChange(sortState: Sort): void {
    const data = this.trainingList.slice();

    this.trainingList = data.sort((a, b) => {
      const isAsc = sortState.direction === 'asc';
      switch (sortState.active) {
        case 'formType':
          return this.compareString(a.formType, b.formType, isAsc);
        case 'topic':
          return this.compareString(a.topic, b.topic, isAsc);
        case 'coach':
          return this.compareString(a.coach, b.coach, isAsc);
        case 'creationDate':
          return this.compareDates(a.creationDate, b.creationDate, isAsc);
        default:
          return 0;
      }
    });

    this.setDataFilteredAndEndPageIdx();
  }

  compareString(str1: string, str2: string, isAsc: boolean): number {
    if (str1 === undefined || str1 === null) {
      return str2 === undefined || str2 === null ? 0 : isAsc ? -1 : 1;
    }
    if (str2 === undefined || str2 === null) {
      return isAsc ? 1 : -1;
    }
    return str1.localeCompare(str2) * (isAsc ? 1 : -1);
  }

  compareDates(dateStr1: string, dateStr2: string, isAsc: boolean): number {
    if (dateStr1 === undefined || dateStr1 === null) {
      return dateStr2 === undefined || dateStr2 === null ? 0 : isAsc ? -1 : 1;
    }
    if (dateStr2 === undefined || dateStr2 === null) {
      return isAsc ? 1 : -1;
    }

    // Parse dates from MM/DD/YYYY format
    const parts1 = dateStr1.split('/');
    const parts2 = dateStr2.split('/');

    const date1 = new Date(+parts1[2], +parts1[0] - 1, +parts1[1]);
    const date2 = new Date(+parts2[2], +parts2[0] - 1, +parts2[1]);

    return (date1.getTime() - date2.getTime()) * (isAsc ? 1 : -1);
  }

  openDetailsModal(item: any): void {
    this.dialog.open(DriverCentralDetailsModalComponent, {
      width: '749px',
      data: {
        formId: item.originalData?.id || '',
        formType: item.formType,
        topic: item.topic,
        coach: item.coach,
        creationDate: item.creationDate,
      },
      panelClass: 'driver-central-details-modal',
    });
  }
}
